import { Injectable } from '@angular/core';

import { environment } from '../environments/environment';

/**
 * Writes the short-lived, domain-scoped cookie consumed by the Medallia embed
 * script on the marketing success page after an applicant completes the Stripe
 * payment step.
 *
 * The cookie is unconditionally scoped to `.triconhomes.com` because that is
 * the only registrable domain the marketing success page is hosted on
 * (`triconresidential.com/success/applicationsubmission/` 301-redirects to
 * `triconhomes.com/success/applicationsubmission/`, dropping any
 * `.triconresidential.com` cookie on the cross-registrable-domain hop).
 *
 * The HRA app is reachable in production under both `secure.triconhomes.com`
 * and `secure.triconresidential.com` (DNS aliases serving the same
 * CloudFront distribution). Browsers reject any `Set-Cookie` whose `Domain=`
 * is not a suffix of the document host, so:
 *   - Users entering HRA via `secure.triconhomes.com` -> cookie is written
 *     and is readable by the marketing reader on `triconhomes.com`. ✅
 *   - Users entering HRA via `secure.triconresidential.com` -> the browser
 *     silently rejects the cookie write (Domain mismatch). The redirect to
 *     `triconhomes.com/success/applicationsubmission/` still happens, but no
 *     survey is shown. ⚠️
 * The long-term fix is for infra to make one HRA host canonical (301 the
 * other) or for marketing to publish the success page on both registrable
 * domains. Tracked separately; not in this code path.
 *
 * On `localhost` the Domain/Secure attributes are omitted so the cookie is
 * still observable for manual verification during development; the marketing
 * site reader path cannot be exercised locally regardless.
 *
 * Cookie name and payload shape are intentionally generic (no Medallia/kampyle
 * branding) — the Medallia embed script is configured marketing-side to read
 * this exact name. The payload value is the URL-encoded JSON body returned
 * verbatim by the survey-metadata endpoint.
 */
@Injectable({ providedIn: 'root' })
export class SurveyCookieService {
  /** Cookie name read by the Medallia embed script on the marketing success page. */
  static readonly COOKIE_NAME = 'tricon_application_survey';
  /**
   * Cookie lifetime in seconds (2 minutes per TP-9217 AC 4). The cookie is
   * written only after a successful payment and immediately before the
   * redirect to the marketing success page, so it only needs to outlive the
   * redirect itself.
   */
  static readonly MAX_AGE_SECONDS = 120;
  /**
   * Cookie Domain attribute on deployed hosts. Matches the eTLD+1 of the
   * marketing success page (`triconhomes.com/success/applicationsubmission/`).
   * Browsers will reject this Domain when the document host is on the
   * `triconresidential.com` registrable domain; that's a known limitation,
   * see the class doc.
   */
  static readonly COOKIE_DOMAIN = '.triconhomes.com';

  writeApplicationSurveyCookie(payload: unknown): void {
    try {
      const value = encodeURIComponent(JSON.stringify(payload));
      const attrs = this.buildAttributes();
      const cookieString = `${SurveyCookieService.COOKIE_NAME}=${value}; ${attrs}`;
      // TP-9217 DIAGNOSTIC: non-prod log of the attribute string (not the
      // encoded payload) so we can verify the Domain/Secure/SameSite combination
      // the browser actually sees during staging E2E.
      if (!environment.production) {
        console.log('[TP-9217] SurveyCookieService.writeApplicationSurveyCookie', {
          cookieName: SurveyCookieService.COOKIE_NAME,
          attrs,
          valueLength: value.length,
          hostname: this.getHostname(),
          domainMatchesHost: this.hostMatchesCookieDomain()
        });
      }
      this.setCookie(cookieString);
    } catch (err) {
      console.warn('[SurveyCookieService] Failed to write application survey cookie', err);
    }
  }

  /**
   * Returns `true` if the current document host is a suffix-match for
   * `COOKIE_DOMAIN` (i.e. on the `triconhomes.com` registrable domain), and
   * therefore the browser will accept the cookie write. Returns `false` for
   * `triconresidential.com` hosts and any other unrecognized host. Exposed
   * primarily for diagnostic logging.
   */
  hostMatchesCookieDomain(): boolean {
    const host = this.getHostname().toLowerCase();
    const bare = SurveyCookieService.COOKIE_DOMAIN.replace(/^\./, '');
    return host === bare || host.endsWith('.' + bare);
  }

  /** Overridable in tests to avoid document.cookie non-configurability. */
  protected setCookie(cookieString: string): void {
    document.cookie = cookieString;
  }

  private buildAttributes(): string {
    const parts: string[] = [
      'Path=/',
      `Max-Age=${SurveyCookieService.MAX_AGE_SECONDS}`,
      `Expires=${new Date(Date.now() + SurveyCookieService.MAX_AGE_SECONDS * 1000).toUTCString()}`
    ];

    if (this.isLocalhost()) {
      // Browsers reject Domain=localhost; also can't require Secure over http.
      parts.push('SameSite=Lax');
    } else {
      parts.push(`Domain=${SurveyCookieService.COOKIE_DOMAIN}`);
      parts.push('Secure');
      parts.push('SameSite=None');
    }

    return parts.join('; ');
  }

  /** Overridable in tests to avoid `window.location.hostname` non-configurability. */
  protected getHostname(): string {
    return (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
  }

  private isLocalhost(): boolean {
    const host = this.getHostname();
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  }
}
